import { useState, useEffect, useCallback, ReactNode } from "react"
import cn from "classnames"
import { LuInfo } from "react-icons/lu"
import { IoIosArrowDown, IoIosArrowForward } from "react-icons/io"
import { Tooltip } from "components/Core"
import usePreRender from "hooks/usePreRender"
import { PiWarningFill } from "react-icons/pi"

interface IFormSection {
  title: string
  number?: number
  helperText?: string
  children: ReactNode
  anyError?: boolean
  closed?: boolean
  className?: string
  classNameContent?: string
}

const FormSection = ({
  title,
  number,
  helperText,
  children,
  closed = true,
  anyError = false,
  className,
  classNameContent,
}: IFormSection) => {
  const { _color } = usePreRender()
  const [is_closed, set_is_closed] = useState(closed)

  useEffect(() => {
    set_is_closed(closed)
  }, [closed])

  const handle_toggle_section = useCallback(() => set_is_closed(!is_closed), [is_closed, set_is_closed])

  return (
    <div className={cn("w-full lg:w-3/5 flex flex-col mb-4", className)}>
      <div className={cn(
        "w-full bg-white rounded-[12px] border border-gray-200 shadow-sm transition-all duration-300",
        is_closed ? "overflow-hidden" : "overflow-visible"
      )}>
        {/* Header Area */}
        <div
          onClick={handle_toggle_section}
          className={cn(
            "w-full px-6 py-4 flex flex-row items-center justify-between cursor-pointer transition-colors select-none",
            "bg-gray-50/80 hover:bg-gray-100/50",
            !is_closed && "border-b border-gray-100",
          )}
        >
          <div className="flex flex-row items-center gap-3">
            <div className={cn("w-1.5 h-5 rounded-full", `bg_${_color}`)}></div>
            <h3 className="text-[14px] font-bold text-gray-700 uppercase tracking-wide">{title}</h3>
            {helperText && (
              <Tooltip content={helperText} align="top">
                <LuInfo className="text-sm text-gray-400 cursor-help" />
              </Tooltip>
            )}
          </div>

          <div className="flex flex-row items-center gap-4">
            {anyError && (
              <PiWarningFill
                className="animate-[scale_2s_linear_infinite] hover:animate-none text-[#CE8400]"
                fontSize={18}
              />
            )}
            <div className="text-gray-400">
              {is_closed ? <IoIosArrowForward className="text-xl" /> : <IoIosArrowDown className="text-xl" />}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div
          className={cn(
            "transition-all duration-500 ease-in-out",
            is_closed ? "max-h-0 opacity-0 pointer-events-none" : "max-h-[5000px] opacity-100",
          )}
        >
          <div className={cn("flex flex-col p-6 lg:p-10", classNameContent)}>{children}</div>
        </div>
      </div>
    </div>
  )
}

export default FormSection
